/**
 * Markdown — the subset the writer emits — parsed to data, and formatted.
 *
 * Two jobs, both pure, both here rather than in `MarkdownEditor.tsx` because
 * `react-refresh/only-export-components` refuses a module that exports both a
 * component and something else, and `eslint --max-warnings=0` makes that fatal.
 * The same split appears in `ui/toast-context.ts` and `shell/navigation.ts`.
 *
 * WHY A PARSER AND NOT A LIBRARY
 * --------------------------------
 * `marked`, `markdown-it` and friends all render to an HTML **string**, which
 * on a React page can only be mounted through `dangerouslySetInnerHTML` — and
 * this is the one screen in the product where the text being rendered was
 * written by a language model and is about to be published on the tenant's own
 * domain. An injection here is not a defaced dashboard; it is a defaced
 * customer website. So `parseMarkdown` answers *data*, the editor turns that
 * data into React elements, and there is no point in the pipeline where a
 * string becomes markup. React escapes every text node it renders, which makes
 * the whole class of attack unreachable rather than filtered.
 *
 * It is also the smaller change: no dependency, and CLAUDE.md asks for a stated
 * reason before one is added.
 *
 * THE SUBSET
 * ------------
 * Exactly what `app/seo/writer.py` produces and what `EditsIn.body_markdown`
 * stores: `##`/`###` headings, paragraphs, `-`/`*` bullets, `1.` numbered
 * items, `**bold**`, `*italic*`, `` `code` `` and `[text](url)` links. Anything
 * else — a table, a fenced block, an image — is not silently dropped: it falls
 * through to a paragraph and the tenant sees the literal source, which is the
 * honest failure. Hiding a line that will be published is the failure that is
 * not.
 *
 * `#` is folded into `h2` rather than rendered as `h1`: the page's `h1` is its
 * title, which is edited in its own field above, and two `h1`s on one published
 * page is the heading defect `app/seo/findings.py` files against a site.
 */

export type Inline =
  | { kind: 'text'; text: string }
  | { kind: 'strong'; text: string }
  | { kind: 'em'; text: string }
  | { kind: 'code'; text: string }
  | { kind: 'link'; text: string; href: string }

export type Block =
  | { kind: 'h2'; spans: Inline[] }
  | { kind: 'h3'; spans: Inline[] }
  | { kind: 'p'; spans: Inline[] }
  | { kind: 'ul'; items: Inline[][] }
  | { kind: 'ol'; items: Inline[][] }

/** What a toolbar button does. */
export type Format = 'bold' | 'italic' | 'h2' | 'h3' | 'link' | 'list'

/** A textarea's selection, and what it becomes after a format is applied. */
export interface Selection {
  start: number
  end: number
}

export interface Edit extends Selection {
  value: string
}

/**
 * Inline syntax, in precedence order.
 *
 * Code first, because backticks quote everything inside them and a `**` in a
 * code span is two asterisks the writer meant literally. Links before emphasis,
 * so `[**bold link**](…)` is a link rather than a stray bracket. `**` before
 * `*`, which alternation gives for free by trying the longer branch first at
 * each position.
 *
 * Every branch is newline-free, so an unclosed marker cannot swallow the rest
 * of the document — it stays a stray asterisk on its own line, which is what
 * the tenant typed.
 */
const INLINE =
  /`([^`\n]+)`|\[([^\]\n]*)\]\(([^)\s]*)\)|\*\*([^*\n]+)\*\*|__([^_\n]+)__|\*([^*\n]+)\*|_([^_\n]+)_/g

const HEADING = /^(#{1,6})\s+(.*)$/
const BULLET = /^[-*]\s+(.*)$/
const NUMBERED = /^\d+[.)]\s+(.*)$/
const LIST_MARKER = /^(?:[-*]\s+|\d+[.)]\s+)/
const HEADING_MARKER = /^#{1,6}\s+/

/** Schemes a rendered link may carry. */
const SAFE_SCHEME = /^(?:https?:|mailto:)/i

/**
 * A link target that is safe to put in an `href`, or null.
 *
 * The **only** place in this file where a value reaches the DOM as something
 * other than text, so it is the only place an attack has to aim at.
 * `javascript:`, `data:` and `vbscript:` are the three that execute, and an
 * allowlist is what keeps a fourth from being discovered later. A rejected
 * target is not dropped — the editor renders the link's label as plain text, so
 * the tenant can see that something was refused.
 *
 * `//host` is refused with the schemes rather than treated as a root-relative
 * path: protocol-relative URLs are off-site and read as if they were not.
 */
export function safeHref(raw: string): string | null {
  const href = raw.trim()
  if (href === '') return null
  // **Both slashes, and the backslash is the one that was missed.** `//host` is
  // protocol-relative and every browser resolves `/\host` the same way — so the
  // `startsWith('/')` below would have passed it through as a same-origin path
  // and rendered a model-written link to somebody else's site as if it were the
  // tenant's own. Checked before the path case, not after.
  if (/^[/\\]{2}/.test(href)) return null
  if (href.startsWith('/') || href.startsWith('#')) return href
  return SAFE_SCHEME.test(href) ? href : null
}

function spansOf(text: string): Inline[] {
  const spans: Inline[] = []
  let at = 0
  INLINE.lastIndex = 0

  let match = INLINE.exec(text)
  while (match !== null) {
    if (match.index > at) spans.push({ kind: 'text', text: text.slice(at, match.index) })

    const code = match[1]
    const label = match[2]
    const target = match[3]
    const strong = match[4] ?? match[5]
    const emphasis = match[6] ?? match[7]

    if (code !== undefined) {
      spans.push({ kind: 'code', text: code })
    } else if (label !== undefined && target !== undefined) {
      const href = safeHref(target)
      spans.push(
        href === null ? { kind: 'text', text: label } : { kind: 'link', text: label, href },
      )
    } else if (strong !== undefined) {
      spans.push({ kind: 'strong', text: strong })
    } else if (emphasis !== undefined) {
      spans.push({ kind: 'em', text: emphasis })
    }

    at = match.index + match[0].length
    match = INLINE.exec(text)
  }

  if (at < text.length) spans.push({ kind: 'text', text: text.slice(at) })
  return spans
}

/**
 * Markdown to blocks.
 *
 * Line-based rather than a grammar, because the subset is line-based: a blank
 * line ends a block, a marker at the start of a line opens one, and everything
 * else joins the paragraph being collected. Consecutive paragraph lines are
 * joined with a space, which is what a markdown writer means by a soft wrap.
 */
export function parseMarkdown(source: string): Block[] {
  const blocks: Block[] = []
  let paragraph: string[] = []
  let items: string[] = []
  let ordered = false

  function flush(): void {
    if (paragraph.length > 0) {
      blocks.push({ kind: 'p', spans: spansOf(paragraph.join(' ')) })
      paragraph = []
    }
    if (items.length > 0) {
      const rendered = items.map(spansOf)
      blocks.push(ordered ? { kind: 'ol', items: rendered } : { kind: 'ul', items: rendered })
      items = []
    }
  }

  for (const raw of source.replace(/\r\n?/g, '\n').split('\n')) {
    const line = raw.trim()
    if (line === '') {
      flush()
      continue
    }

    const heading = HEADING.exec(line)
    if (heading !== null) {
      flush()
      const spans = spansOf(heading[2] ?? '')
      blocks.push((heading[1] ?? '').length >= 3 ? { kind: 'h3', spans } : { kind: 'h2', spans })
      continue
    }

    const bullet = BULLET.exec(line)
    if (bullet !== null) {
      // A paragraph ends where a list starts, and a bulleted list is not a
      // continuation of a numbered one.
      if (paragraph.length > 0 || (items.length > 0 && ordered)) flush()
      ordered = false
      items.push(bullet[1] ?? '')
      continue
    }

    const numbered = NUMBERED.exec(line)
    if (numbered !== null) {
      if (paragraph.length > 0 || (items.length > 0 && !ordered)) flush()
      ordered = true
      items.push(numbered[1] ?? '')
      continue
    }

    if (items.length > 0) flush()
    paragraph.push(line)
  }

  flush()
  return blocks
}

/** The whole lines a selection touches, as a range over `value`. */
function lineRange(value: string, selection: Selection): Selection {
  const start = value.lastIndexOf('\n', selection.start - 1) + 1
  const newline = value.indexOf('\n', selection.end)
  return { start, end: newline === -1 ? value.length : newline }
}

function replace(value: string, range: Selection, text: string): string {
  return value.slice(0, range.start) + text + value.slice(range.end)
}

function surround(value: string, selection: Selection, marker: string): Edit {
  const chosen = value.slice(selection.start, selection.end)
  return {
    value: replace(value, selection, `${marker}${chosen}${marker}`),
    // Keep the words selected rather than the markers, so pressing **B** twice
    // in a row is two decisions about the same words and not one about the
    // asterisks the first press added.
    start: selection.start + marker.length,
    end: selection.start + marker.length + chosen.length,
  }
}

function prefixLines(value: string, selection: Selection, prefix: string): Edit {
  const range = lineRange(value, selection)
  const rewritten = value
    .slice(range.start, range.end)
    .split('\n')
    .map((line) => `${prefix}${line.replace(HEADING_MARKER, '').replace(LIST_MARKER, '')}`)
    .join('\n')
  return {
    value: replace(value, range, rewritten),
    start: range.start,
    end: range.start + rewritten.length,
  }
}

/** What the placeholder URL is, so a caller can select it for the user. */
const LINK_PLACEHOLDER = 'https://'

/**
 * One toolbar press, as a pure function over the text and the selection.
 *
 * Pure so that the editor is testable without a caret: the component reads
 * `selectionStart`/`selectionEnd` off the textarea, hands them here, and writes
 * the answer back. Every branch returns a selection as well as a value, because
 * a formatter that leaves the caret at position zero makes the second press
 * unusable.
 */
export function applyFormat(format: Format, value: string, selection: Selection): Edit {
  switch (format) {
    case 'bold':
      return surround(value, selection, '**')
    case 'italic':
      return surround(value, selection, '*')
    case 'h2':
      return prefixLines(value, selection, '## ')
    case 'h3':
      return prefixLines(value, selection, '### ')
    case 'list':
      return prefixLines(value, selection, '- ')
    case 'link': {
      const chosen = value.slice(selection.start, selection.end)
      const label = chosen === '' ? 'link text' : chosen
      const inserted = `[${label}](${LINK_PLACEHOLDER})`
      // The URL is selected, not the label: the label is either what was
      // highlighted or an obvious placeholder, and the address is the part
      // nobody can guess for the tenant.
      const opens = selection.start + label.length + 3
      return {
        value: replace(value, selection, inserted),
        start: opens,
        end: opens + LINK_PLACEHOLDER.length,
      }
    }
  }
}
